import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/useAuth';
import Header from '../../../components/Header/Header';
import { appsAPI } from '../../apps/api/appsAPI';
import './StudioLandingPage.css';

/* Placeholder subject/test apps when API returns few or none */
const FALLBACK_PRODUCTIVITY = [
  { id: 'tmua', exam_name: 'TMUA', title: 'TMUA', subject: 'Maths', app_type: 'productivity' },
  { id: 'ucat', exam_name: 'UCAT', title: 'UCAT', subject: 'Admissions', app_type: 'productivity' },
  { id: 'lnat', exam_name: 'LNAT', title: 'LNAT', subject: 'Law', app_type: 'productivity' },
  { id: 'step', exam_name: 'STEP', title: 'STEP', subject: 'Maths', app_type: 'productivity' },
  { id: 'bmat', exam_name: 'BMAT', title: 'BMAT', subject: 'Medicine', app_type: 'productivity' },
];

const StudioLandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isLoggedIn = !!user;

  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchApps = async () => {
      try {
        const data = await appsAPI.getAppsPublic();
        setApps(Array.isArray(data) ? data : []);
      } catch {
        setApps([]);
      } finally {
        setLoading(false);
      }
    };
    fetchApps();
  }, []);

  const handlePrimaryCTA = () => {
    if (isLoggedIn) {
      navigate('/studio');
    } else {
      navigate('/login', { state: { returnTo: '/studio' } });
    }
  };

  const handleSecondaryCTA = () => {
    if (isLoggedIn) {
      navigate('/apps-store');
    } else {
      navigate('/login', { state: { returnTo: '/apps-store' } });
    }
  };

  const handleLogoClick = () => navigate('/');
  const handleSignIn = () => navigate('/login');

  const experienceApps = apps.filter((a) => a.app_type === 'case');
  const productivityApps = apps.filter((a) => a.app_type === 'tutorial' || a.app_type === 'productivity' || a.app_type === 'utility');
  const useFallback = productivityApps.length < 3;
  const displayProductivity = useFallback
    ? [...productivityApps, ...FALLBACK_PRODUCTIVITY.filter((f) => !productivityApps.some((p) => p.exam_name === f.exam_name))]
    : productivityApps;

  // Prefer short names (e.g. TMUA, UCAT) for the text-on-black productivity icons
  const hasShortName = (app) => {
    const name = (app.exam_name || app.title || '').toString();
    return name.length <= 6;
  };
  const shortNameProductivity = displayProductivity.filter(hasShortName);

  // Curated selection: cases first (nice imagery), then short-name productivity (clean black text icons)
  const maxDisplay = 32;
  const mixedApps = [
    ...experienceApps.slice(0, maxDisplay).map((a) => ({ ...a, _type: 'experience' })),
    ...shortNameProductivity.slice(0, maxDisplay - experienceApps.length).map((a) => ({ ...a, _type: 'productivity' })),
  ].slice(0, maxDisplay);

  return (
    <div className="studio-landing-page">
      <Header
        variant="dark"
        customTitle="ivori Studio" 
        onNavigate={(page) => {
          if (page === 'login') handleSignIn();
          if (page === 'home') handleLogoClick();
        }}
      />

      <main className="studio-landing-main">
        {/* Hero: ivori Studio logo + tagline (App Store format) */}
        <section className="studio-landing-hero">
          <div className="studio-landing-hero-inner">
            <div className="studio-landing-logo-block" onClick={handleLogoClick} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && handleLogoClick()}>
              <div className="studio-landing-logo-icon">
                <span className="studio-landing-logo-text">Ps.</span>
              </div>
            </div>
            <h2 className="studio-landing-brand">ivori Studio</h2>
            <p className="studio-landing-tagline">Together we turn apps into opportunities</p>
          </div>
        </section>

        {/* App grid: full width, flows left to right */}
        <section className="studio-landing-grid-section">
          <div className="studio-landing-grid">
            {loading ? (
              <div className="studio-landing-loading">Loading apps...</div>
            ) : (
              mixedApps.map((app, idx) => {
                if (app._type === 'productivity') {
                  const label = app.exam_name || app.title || 'App';
                  return (
                    <div key={`p-${app.id}-${idx}`} className="studio-landing-icon-wrap">
                      <div className="studio-landing-icon studio-landing-icon-productivity">
                        <span>{label}</span>
                      </div>
                    </div>
                  );
                }
                const img = app.images?.medium || app.images?.large || app.images?.small || '/logo192.png';
                return (
                  <div key={`${app.id}-${idx}`} className="studio-landing-icon-wrap">
                    <div className="studio-landing-icon" style={{ backgroundImage: `url(${img})` }} />
                  </div>
                );
              })
            )}
          </div>
        </section>

        {/* Bottom: same text and CTAs */}
        <section className="studio-landing-cta-section">
          <h1 className="studio-landing-cta-title">
            Together we turn apps into opportunities
          </h1>
          <p className="studio-landing-copy">
            ivori Studio helps creators build cinematic learning experiences from start to finish.
            Design, generate, and publish apps that change how students learn.
          </p>
          <div className="studio-landing-actions">
            <button
              type="button"
              className="studio-landing-btn-primary"
              onClick={handlePrimaryCTA}
            >
              Start with ivori Studio
            </button>
            <button
              type="button"
              className="studio-landing-btn-secondary"
              onClick={handleSecondaryCTA}
            >
              Immediately start building with ivori Studio
            </button>
          </div>
        </section>
      </main>
    </div>
  );
};

export default StudioLandingPage;
